/**
 * Imports the necessary dependencies for rendering a React component that displays a smart table.
 * @module React
 * @module CSmartTable - A component from the "@coreui/react-pro" library that renders a smart table.
 * @module useTranslation - A hook from the "react-i18next" library that provides translation capabilities.
 * @module Card - A component from the "react-bootstrap" library that renders a card.
 * @module CustomLoadingHTML - A custom component that renders a loading animation.
 * @module EmptyCell - A custom component that renders an empty cell.
 * @module moment - A library for parsing, validating, manipulating, and formatting dates.
 */
import { CSmartTable } from "@coreui/react-pro";
import moment from "moment";
import React from "react";
import Card from "react-bootstrap/Card";
import { useTranslation } from "react-i18next";
import Select from "react-select";

import { getCloudinaryMediaBaseUrl } from "../../../config/cloudinaryBase";
import { pickEventDisplayNumber } from "../../../helpers/eventDisplayNumber";
import { resolveCloudinaryImageSrc } from "../../../helpers/helper.service";
import Constant from "../../../util/constant";
import CustomLoadingHTML from "../../customLoadingHTML/customLoadingHTML";
import EmptyCell from "../../emptyCell/emptyCell";
import AvatarImageWithFallback from "../../avatarImageWithFallback/AvatarImageWithFallback";

const CurrentEventTabComponent = (props) => {
  /**
   * Destructures the necessary props from the `props` object and assigns them to
   * individual variables for easier access and readability.
   * @param {{object}} props - The props object containing all the necessary data.
   * @returns None
   */
  const { t } = useTranslation();
  const {
    eventsData,
    columns,
    placeHolderShimer,
    onTableFilterChange,
    noDataTitle,
    noDataDesc,
    clearSearchButton,
    smarttableref,
    noDataFound,
    noDataAddButtonTitle,
    emptyDataAddButton,
    dimensions,
    searchFilterClear,
    searchValue,
    loading,
    tableContainerRef,
    tableBodyScrollHeight,
    emptyImg,
    emptyTitle,
    emptyDataDesc,
    itemsPerPage,
    onRowClick,
    addEventFunction,
    emptyDataDescTow,
    noDataDescTwo,
    screenWidth,
    showAdminEventFilters,
    eventScopeOptions,
    eventScopeValue,
    onEventScopeChange,
    clubFilterOptions,
    selectedClubFilter,
    onClubFilterChange,
    showClubsShortcut,
    onNavigateToClubs,
    hideAddEvent,
  } = props;

  /**
   * Constructs the Cloudinary URL by concatenating the environment variables
   * REACT_APP_CLOUDINARY_URL, REACT_APP_CLOUDINARY_NAME, and REACT_APP_CLOUDINARY_SUBFOLDER.
   * @returns The constructed Cloudinary URL.
   */
  const CLOUDINARY_URL = getCloudinaryMediaBaseUrl();
  const isMobileView = Number(screenWidth) <= 576;

  return (
    <React.Fragment>
      <div className="eventsTab">
        <div className="events">
          <Card className="mainCard mainCardWrapper">
            <Card.Body className="p-0 activeTab">
              <div className="mainCardBody">
                <div className="Card-Button events-add-toolbar d-flex flex-wrap gap-2 align-items-center">
                  {!hideAddEvent && (
                    <button
                      color="primary"
                      className="btn-block btn btn-primary fillButton"
                      type="button"
                      onClick={() => {
                        addEventFunction();
                      }}
                    >
                      <i className="bx bxs-plus-circle"></i>
                      {t("ADD_EVENT")}
                    </button>
                  )}
                  {showClubsShortcut && (
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={() => onNavigateToClubs?.()}
                    >
                      <i className="bx bxs-buildings me-1"></i>
                      {t("CLUBS")}
                    </button>
                  )}
                  {showAdminEventFilters && (
                    <>
                      <div style={{ minWidth: 200, flex: "1 1 200px" }}>
                        <Select
                          className="react-select-container singleSelecter"
                          classNamePrefix="react-select"
                          options={eventScopeOptions}
                          value={eventScopeValue}
                          onChange={onEventScopeChange}
                          placeholder={t("EVENT_FILTER_LABEL")}
                          isSearchable={false}
                        />
                      </div>
                      {eventScopeValue?.value === "club" && (
                        <div style={{ minWidth: 220, flex: "1 1 220px" }}>
                          <Select
                            className="react-select-container singleSelecter"
                            classNamePrefix="react-select"
                            options={clubFilterOptions}
                            value={selectedClubFilter}
                            onChange={onClubFilterChange}
                            placeholder={t("EVENT_FILTER_CLUB_PLACEHOLDER")}
                            isClearable
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>

                <CSmartTable
                  ref={smarttableref}
                  clickableRows
                  columns={columns}
                  columnFilter={true}
                  items={eventsData}
                  itemsPerPage={itemsPerPage}
                  scopedColumns={{
                    image: (item) => {
                      if (isMobileView) return <td></td>;
                      return (
                        <td>
                          <div className="eventProfile tdWrapper">
                            {placeHolderShimer && (
                              <div className="shimer"></div>
                            )}
                            <img
                              src={resolveCloudinaryImageSrc(
                                item?.image,
                                CLOUDINARY_URL
                              )}
                              alt=""
                            />
                          </div>
                        </td>
                      );
                    },
                    title: (item) => {
                      const hasClubEvent = Boolean(item?.clubid || item?.clubId);
                      const eventDisplayNumber = pickEventDisplayNumber(item);
                      return (
                        <td>
                          <div className="eventDetail tdWrapper">
                            {placeHolderShimer && (
                              <div className="shimer"></div>
                            )}
                            {!isMobileView && <span>{item?.title}</span>}
                            {!isMobileView && item?.is18Plus === true ? (
                              <span className="badge bg-warning text-dark ms-2">
                                18+
                              </span>
                            ) : null}
                            {!isMobileView && eventDisplayNumber ? (
                              <div className="event-number-list">
                                {t("EVENT_NUMBER")}=#{eventDisplayNumber}
                              </div>
                            ) : null}
                            {isMobileView && (
                              <>
                                <div className="mb-2">
                                  <img
                                    src={resolveCloudinaryImageSrc(
                                      item?.image,
                                      CLOUDINARY_URL
                                    )}
                                    alt={item?.title || "event-image"}
                                    width={48}
                                    height={48}
                                    style={{ objectFit: "cover", borderRadius: 6 }}
                                  />
                                </div>
                                <p className="mb-1">{item?.title}</p>
                                {eventDisplayNumber ? (
                                  <p className="mb-1 event-number-list">
                                    {t("EVENT_NUMBER")}=#{eventDisplayNumber}
                                  </p>
                                ) : null}
                                <p className="mb-1">
                                  {moment(item?.startdatetime).format("DD MMM YY, h:mm A")}
                                </p>
                                {hasClubEvent && item?.clubLabel ? (
                                  <p className="mb-0">{item.clubLabel}</p>
                                ) : null}
                              </>
                            )}
                          </div>
                        </td>
                      );
                    },
                    location: (item) => {
                      if (isMobileView) return <td></td>;
                      return (
                        <td>
                          <div className="address tdWrapper">
                            {placeHolderShimer && (
                              <div className="shimer"></div>
                            )}
                            {item?.location}
                          </div>
                        </td>
                      );
                    },
                    languages: (item) => {
                      if (isMobileView) return <td></td>;
                      return (
                        <td>
                          <div className="hideTd tdWrapper">
                            {placeHolderShimer && (
                              <div className="shimer"></div>
                            )}

                            {item?.languages}
                          </div>
                        </td>
                      );
                    },
                    artistList: (item) => {
                      if (isMobileView) return <td></td>;
                      return (
                        <td>
                          <div className="hideTd tdWrapper">
                            {placeHolderShimer && (
                              <div className="shimer"></div>
                            )}
                            {item?.artistList}
                          </div>
                        </td>
                      );
                    },
                    categoryList: (item) => {
                      if (isMobileView) return <td></td>;
                      return (
                        <td>
                          <div className="hideTd tdWrapper">
                            {placeHolderShimer && (
                              <div className="shimer"></div>
                            )}
                            {item?.categoryList}
                          </div>
                        </td>
                      );
                    },
                    dateList: (item) => {
                      if (isMobileView) return <td></td>;
                      return (
                        <td>
                          <div className="hideTd tdWrapper">
                            {placeHolderShimer && (
                              <div className="shimer"></div>
                            )}
                            {item?.dateList}
                          </div>
                        </td>
                      );
                    },
                    artistsDetails: (item) => {
                      if (isMobileView) return <td></td>;
                      return (
                        <td>
                          <div className="artistRepeat tdWrapper">
                            {placeHolderShimer && (
                              <div className="shimer"></div>
                            )}
                            {item?.artistsDetails?.map((item) => {
                              return (
                                <>
                                  <div className="artist">
                                    <img
                                      width={30}
                                      height={30}
                                      src={resolveCloudinaryImageSrc(
                                        item?.image,
                                        CLOUDINARY_URL
                                      )}
                                      alt=""
                                    />
                                    <p>{item?.name}</p>
                                  </div>
                                </>
                              );
                            })}
                          </div>
                        </td>
                      );
                    },
                    category: (item, e) => {
                      if (isMobileView) return <td></td>;
                      return (
                        <td>
                          {item?.category?.map((item) => {
                            return (
                              <>
                                <div className="category tdWrapper">
                                  {placeHolderShimer && (
                                    <div className="shimer"></div>
                                  )}
                                  <p>{item?.title}</p>
                                </div>
                              </>
                            );
                          })}
                        </td>
                      );
                    },
                    startdatetime: (item, e) => {
                      if (isMobileView) return <td></td>;
                      return (
                        <td>
                          <div className="address tdWrapper">
                            {placeHolderShimer && (
                              <div className="shimer"></div>
                            )}
                            {moment(item?.startdatetime).format(
                              "DD MMM YY, h:mm A"
                            )}
                            {t("TO")}
                            {moment(item?.enddatetime).format(
                              "DD MMM YY, h:mm A"
                            )}
                          </div>
                        </td>
                      );
                    },
                    ticketprice: (item, e) => {
                      if (isMobileView) return <td></td>;
                      const ticketPrices = Array.isArray(item?.tickets)
                        ? item?.tickets?.map((ticket) => ticket.ticketprice)
                        : [0];

                      return (
                        <td>
                          <div className="tdWrapper">
                            {placeHolderShimer && (
                              <div className="shimer"></div>
                            )}
                            {dimensions.width < 1631 && t("TICKET_PRICE_")} ₹{" "}
                            {item?.ticketprice
                              ? item?.ticketprice
                              : `${Math.min(...ticketPrices)} - ${Math.max(
                                ...ticketPrices
                              )}`}
                          </div>
                        </td>
                      );
                    },
                    ticketssold: (item, e) => {
                      if (isMobileView) return <td></td>;
                      const sumTotalTickets = Array.isArray(item?.tickets)
                        ? item?.tickets?.reduce(
                          (acc, ticket) => acc + ticket.totaltickets,
                          0
                        )
                        : 0;

                      const sumTicketssold = Array.isArray(item?.tickets)
                        ? item?.tickets?.reduce(
                          (acc, ticket) => acc + ticket.ticketssold,
                          0
                        )
                        : 0;

                      return (
                        <td>
                          <div className="tdWrapper ticketSold">
                            {placeHolderShimer && (
                              <div className="shimer"></div>
                            )}
                            {dimensions.width < 1631 && t("TICKETS_SOLD_")}
                            <span>
                              {" "}
                              {item?.ticketssold
                                ? item?.ticketssold
                                : sumTicketssold}
                              {""} / {""}
                            </span>
                            <span className="tcketTotal">
                              {item?.totaltickets
                                ? item?.totaltickets
                                : sumTotalTickets}
                            </span>
                          </div>
                        </td>
                      );
                    },
                    eventstatus: (item, e) => {
                      if (isMobileView) return <td></td>;
                      return (
                        <td>
                          <div
                            className={
                              item?.eventstatus === Constant.GENERALKEYS.ONGOING
                                ? "onGoing tdWrapper"
                                : item?.eventstatus ===
                                  Constant.GENERALKEYS.UPCOMING
                                  ? "upComing tdWrapper"
                                  : item?.eventstatus ===
                                    Constant.GENERALKEYS.CANCEL
                                    ? "cancelled tdWrapper"
                                    : item?.eventstatus ===
                                      Constant.GENERALKEYS.COMPELETED
                                      ? "completed tdWrapper"
                                      : "tdWrapper"
                            }
                          >
                            {placeHolderShimer && (
                              <div className="shimer"></div>
                            )}
                            {item?.eventstatus}
                          </div>
                        </td>
                      );
                    },
                    organizer: (item, e) => {
                      if (isMobileView) return <td></td>;
                      return (
                        <td>
                          <div className="eventOrganiser tdWrapper">
                            {placeHolderShimer && (
                              <div className="shimer"></div>
                            )}
                            {/* <img
                              width={30}
                              height={30}
                              src={
                                userImage === ""
                                  ? CLOUDINARY_URL + "kaqwwmj7fxy7sfqvdnnz.png"
                                  : CLOUDINARY_URL + userImage
                              }
                              alt=""
                            />
                            <p>{userName}</p> */}
                            {item?.organizer?.map((org, idx) => (
                              <React.Fragment
                                key={org?._id ?? org?.email ?? `org-${idx}`}
                              >
                                <AvatarImageWithFallback
                                  src={resolveCloudinaryImageSrc(
                                    org?.image,
                                    CLOUDINARY_URL
                                  )}
                                  alt={org?.fullname || ""}
                                  width={30}
                                  height={30}
                                />
                                <p>{org?.fullname}</p>
                              </React.Fragment>
                            ))}
                            <i className="mdi mdi-chevron-right iconRight" />
                          </div>
                        </td>
                      );
                    },
                    clubLabel: (item) => {
                      if (isMobileView) return <td></td>;
                      return <td>{item?.clubLabel || "-"}</td>;
                    },
                  }}
                  tableFilter
                  onTableFilterChange={onTableFilterChange}
                  tableFilterLabel={
                    searchValue?.length > 0 && (
                      <button
                        onClick={searchFilterClear}
                        className="btn-close"
                      ></button>
                    )
                  }
                  tableFilterPlaceholder={screenWidth > 576
                    ? t(
                      "SEARCH_EVENTS"
                    )
                    : t("SEARCH_BY_EVENTS_DETAILS")}
                  tableProps={{
                    className: "mainTable",
                    responsive: true,
                    striped: false,
                    hover: false,
                  }}
                  onRowClick={(item) => {
                    onRowClick(item);
                  }}
                  noItemsLabel={
                    !placeHolderShimer && (
                      <EmptyCell
                        emptyItems={eventsData}
                        noDataTitle={
                          eventsData?.length === 0 ? noDataFound : noDataTitle
                        }
                        emptyImg={emptyImg}
                        emptyTitle={emptyTitle}
                        emptyDataDesc={emptyDataDesc}
                        noDataAddButton={
                          !hideAddEvent && eventsData?.length === 0
                        }
                        noDataAddButtonTitle={noDataAddButtonTitle}
                        noDataDesc={noDataDesc}
                        emptyDataDescTow={emptyDataDescTow}
                        emptyDataAddButton={emptyDataAddButton}
                        clearSearchButton={clearSearchButton}
                        noDataDescTwo={noDataDescTwo}
                      />
                    )
                  }
                  tableBodyProps={{
                    className: "align-middle Table-Body-scroll main",
                    style: {
                      height: tableBodyScrollHeight,
                    },
                    ref: tableContainerRef,
                  }}
                />
                {loading && <CustomLoadingHTML />}
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
    </React.Fragment>
  );
};

export default CurrentEventTabComponent;
